// Metric Card Component
import { LucideIcon, TrendingUp, TrendingDown } from 'lucide-react';
import { formatCurrency, formatNumber, formatPercentage } from '../utils/calculations';

interface MetricCardProps {
  title: string;
  titleAr: string;
  value: number;
  icon: LucideIcon;
  format?: 'currency' | 'number' | 'percentage';
  trend?: number;
  subtitle?: string;
  color?: 'green' | 'blue' | 'orange' | 'purple' | 'red' | 'cyan';
}

const colorClasses = {
  green: 'from-emerald-500 to-teal-600',
  blue: 'from-blue-500 to-cyan-600',
  orange: 'from-orange-500 to-red-600',
  purple: 'from-purple-500 to-violet-600',
  red: 'from-red-500 to-rose-600',
  cyan: 'from-cyan-500 to-blue-600',
};

const iconBgClasses = {
  green: 'bg-emerald-100 dark:bg-emerald-900/30',
  blue: 'bg-blue-100 dark:bg-blue-900/30',
  orange: 'bg-orange-100 dark:bg-orange-900/30',
  purple: 'bg-purple-100 dark:bg-purple-900/30',
  red: 'bg-red-100 dark:bg-red-900/30',
  cyan: 'bg-cyan-100 dark:bg-cyan-900/30',
};

const iconColorClasses = {
  green: 'text-emerald-600 dark:text-emerald-400',
  blue: 'text-blue-600 dark:text-blue-400',
  orange: 'text-orange-600 dark:text-orange-400',
  purple: 'text-purple-600 dark:text-purple-400',
  red: 'text-red-600 dark:text-red-400',
  cyan: 'text-cyan-600 dark:text-cyan-400',
};

export function MetricCard({
  title,
  titleAr,
  value,
  icon: Icon,
  format = 'number',
  trend,
  subtitle,
  color = 'blue',
}: MetricCardProps) {
  const formattedValue = 
    format === 'currency' ? formatCurrency(value) :
    format === 'percentage' ? formatPercentage(value) :
    formatNumber(value);

  const trendFormatted = trend !== undefined ? formatPercentage(Math.abs(trend)) : null;
  const trendPositive = trend !== undefined && trend >= 0;

  return (
    <div className="relative overflow-hidden bg-card border border-border rounded-lg p-6 hover:shadow-md transition-shadow">
      {/* Background gradient */}
      <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${colorClasses[color]} opacity-10 rounded-full blur-2xl`} />
      
      <div className="relative flex items-start justify-between">
        <div className="flex-1">
          <div className="flex flex-col gap-1 mb-3">
            <p className="text-sm font-medium text-muted-foreground font-arabic">
              {titleAr}
            </p>
            <p className="text-sm text-muted-foreground">
              {title}
            </p>
          </div>

          <p className="text-3xl font-bold text-foreground mb-2">
            {formattedValue}
          </p>

          {subtitle && (
            <p className="text-xs text-muted-foreground">
              {subtitle}
            </p>
          )}

          {trendFormatted && (
            <div className="flex items-center gap-2 mt-2">
              {trendPositive ? (
                <TrendingUp className="w-4 h-4 text-emerald-600" />
              ) : (
                <TrendingDown className="w-4 h-4 text-red-600" />
              )}
              <span className={`text-sm font-medium ${trendPositive ? 'text-emerald-600' : 'text-red-600'}`}>
                {trendFormatted}
              </span>
              <span className="text-xs text-muted-foreground">
                vs période précédente
              </span>
            </div>
          )}
        </div>

        <div className={`${iconBgClasses[color]} p-3 rounded-xl`}>
          <Icon className={`w-6 h-6 ${iconColorClasses[color]}`} />
        </div>
      </div>
    </div>
  );
}
